import { RANKING } from './constants';
import { areaKeysForPost, postInArea } from './geo';
import type { AreaTile, OpenEvent, RankingState, TileSummary, TrackedPost } from './types';

/** Exponential decay: the score loses half its value every half-life. */
export function decay(
  score: number,
  fromMs: number,
  toMs: number,
  halfLifeMs: number = RANKING.halfLifeMs,
): number {
  if (toMs <= fromMs) return score;
  return score * 2 ** (-(toMs - fromMs) / halfLifeMs);
}

export function currentScore(p: { score: number; scoredAt: number }, now: number): number {
  return decay(p.score, p.scoredAt, now);
}

export function emptyRankingState(now = 0): RankingState {
  return {
    version: 1,
    postsCursor: null,
    opensCursor: null,
    posts: {},
    areaHashes: {},
    updatedAt: now,
  };
}

/** Sums open weights per post. Opens beyond the per-uid cap in one run are ignored. */
export function aggregateOpens(opens: readonly OpenEvent[]): Map<string, number> {
  const perUid = new Map<string, number>();
  const weights = new Map<string, number>();
  for (const o of opens) {
    const n = (perUid.get(o.uid) ?? 0) + 1;
    perUid.set(o.uid, n);
    if (n > RANKING.maxOpensPerUidPerRun) continue;
    const w = o.anon ? RANKING.anonymousWeight : RANKING.signedInWeight;
    weights.set(o.postId, (weights.get(o.postId) ?? 0) + w);
  }
  return weights;
}

function fnv1a(str: string, seed: number): string {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Fingerprint of an area board: tile order plus each tile's weight relative to the top tile,
 * bucketed so that small score drift doesn't trigger a rewrite (every rewrite costs each viewer a read).
 */
export function hashTiles(tiles: readonly AreaTile[]): string {
  const max = tiles[0]?.score || 1;
  const parts = tiles
    .map((t) => `${t.id}:${Math.round(t.score / max / RANKING.weightBucket)}`)
    .join('|');
  return `${tiles.length}-${fnv1a(parts, 0x811c9dc5)}${fnv1a(parts, 0x01234567)}`;
}

function toAreaTile(p: TrackedPost, score: number): AreaTile {
  return {
    id: p.id,
    title: p.title,
    excerpt: p.excerpt ?? '',
    thumb: p.thumb,
    author: p.author,
    isAgent: p.isAgent,
    g5: p.g5,
    createdAt: p.createdAt,
    score: Math.round(score * 10_000) / 10_000,
  };
}

/** Top tiles for an area board from the tracked posts. */
export function tilesForArea(posts: readonly TrackedPost[], key: string, now: number): AreaTile[] {
  return posts
    .filter((p) => postInArea(p.g5, key))
    .map((p) => ({ p, s: currentScore(p, now) }))
    .filter((x) => x.s >= RANKING.minScore)
    .sort((a, b) => b.s - a.s || b.p.createdAt - a.p.createdAt)
    .slice(0, RANKING.tilesPerArea)
    .map((x) => toAreaTile(x.p, x.s));
}

export interface RankStepInput {
  state: RankingState;
  now: number;
  /** Posts that went live since the last run. */
  newPosts: readonly TileSummary[];
  /** Untracked posts that got opened (e.g. via a shared link) and are still live. */
  revived: readonly TileSummary[];
  opens: readonly OpenEvent[];
  /** Posts hidden or removed by moderation since the last run. */
  removedIds: readonly string[];
}

export interface AreaWrite {
  key: string;
  tiles: AreaTile[];
}

export interface RankStepOutput {
  state: RankingState;
  /** Only the area boards whose content changed meaningfully. */
  areaWrites: AreaWrite[];
}

/**
 * One run of the ranking job, as a pure function: fold new opens into decayed scores, track new
 * posts, drop removed/stale ones, and recompute the area boards that were touched. Cursors are
 * advanced by the caller.
 */
export function rankStep(input: RankStepInput): RankStepOutput {
  const { now } = input;
  const posts: Record<string, TrackedPost> = { ...input.state.posts };
  const touched = new Set<string>();
  const touch = (g5: string | null) => {
    for (const k of areaKeysForPost(g5)) touched.add(k);
  };
  const drop = (p: TrackedPost) => {
    if (p.score > 0) touch(p.g5);
    delete posts[p.id];
  };

  const removed = new Set(input.removedIds);
  for (const id of removed) {
    const p = posts[id];
    if (p) {
      touch(p.g5);
      delete posts[id];
    }
  }

  for (const s of [...input.newPosts, ...input.revived]) {
    if (removed.has(s.id) || posts[s.id]) continue;
    posts[s.id] = { ...s, score: 0, scoredAt: now };
  }

  for (const [id, w] of aggregateOpens(input.opens)) {
    const p = posts[id];
    if (!p) continue;
    posts[id] = { ...p, score: currentScore(p, now) + w, scoredAt: now };
    touch(p.g5);
  }

  for (const p of Object.values(posts)) {
    const age = now - p.createdAt;
    const s = currentScore(p, now);
    if (age > RANKING.maxAgeMs || (s < RANKING.minScore && age > RANKING.unscoredGraceMs)) drop(p);
  }

  const remaining = Object.values(posts);
  if (remaining.length > RANKING.maxTrackedPosts) {
    remaining
      .sort((a, b) => currentScore(a, now) - currentScore(b, now) || a.createdAt - b.createdAt)
      .slice(0, remaining.length - RANKING.maxTrackedPosts)
      .forEach(drop);
  }

  const tracked = Object.values(posts);
  const areaHashes = { ...input.state.areaHashes };
  const areaWrites: AreaWrite[] = [];
  for (const key of [...touched].sort()) {
    const tiles = tilesForArea(tracked, key, now);
    const prev = areaHashes[key];
    if (tiles.length === 0) {
      if (prev === undefined) continue;
      delete areaHashes[key];
    } else {
      const hash = hashTiles(tiles);
      if (prev === hash) continue;
      areaHashes[key] = hash;
    }
    areaWrites.push({ key, tiles });
  }

  return {
    state: { ...input.state, posts, areaHashes, updatedAt: now },
    areaWrites,
  };
}
